import { useState, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import { adminApi } from '../../services/adminApi';
import { Headphones, Loader2, AlertCircle, Clock, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';

const PRIORITY_COLORS = { High: 'bg-red-500/10 text-red-400 border-red-500/20', Medium: 'bg-amber-500/10 text-amber-400 border-amber-500/20', Low: 'bg-blue-500/10 text-blue-400 border-blue-500/20' };
const STATUS_COLUMNS = ['Open', 'In Progress', 'Resolved'];
const STATUS_ICONS = { Open: AlertCircle, 'In Progress': Clock, Resolved: CheckCircle2 };
const STATUS_COLORS = { Open: '#ef4444', 'In Progress': '#f59e0b', Resolved: '#10b981' };

export default function Support() {
  const ctx = useOutletContext();
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('kanban');
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    adminApi.getSupportTickets().then(d => { setTickets(d); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="w-8 h-8 animate-spin text-blue-400" /></div>;

  const updateStatus = async (id, status) => {
    setTickets(prev => prev.map(t => t._id === id ? { ...t, status } : t));
    try {
      await adminApi.updateTicketStatus(id, status);
      toast.success(`Ticket → ${status}`);
    } catch {
      toast.error('Failed to update ticket');
    }
  };

  return (
    <div data-testid="admin-support">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold" style={{ fontFamily: 'Chivo, sans-serif' }}>Support Tickets</h1>
          <p className={`text-sm ${ctx.textSecondary} mt-1`}>{tickets.length} tickets · {tickets.filter(t => t.status === 'Open').length} open</p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => setView('kanban')} className={`px-3 py-1.5 rounded-lg text-xs font-medium ${view === 'kanban' ? 'bg-blue-600 text-white' : `${ctx.hoverBg} ${ctx.textSecondary}`}`}>Kanban</button>
          <button onClick={() => setView('list')} className={`px-3 py-1.5 rounded-lg text-xs font-medium ${view === 'list' ? 'bg-blue-600 text-white' : `${ctx.hoverBg} ${ctx.textSecondary}`}`}>List</button>
        </div>
      </div>

      {view === 'kanban' ? (
        <div className="grid md:grid-cols-3 gap-4" data-testid="support-kanban">
          {STATUS_COLUMNS.map(col => {
            const Icon = STATUS_ICONS[col];
            const colTickets = tickets.filter(t => t.status === col);
            const colId = col.toLowerCase().replace(/\s+/g, '-');
            return (
              <div key={col} data-testid={`kanban-col-${colId}`} className={`${ctx.cardBg} rounded-xl border ${ctx.borderColor} p-4`}>
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <Icon className="w-4 h-4" style={{ color: STATUS_COLORS[col] }} />
                    <h3 className="font-semibold text-sm">{col}</h3>
                  </div>
                  <span className={`text-xs px-2 py-0.5 rounded-full ${ctx.hoverBg} ${ctx.textSecondary}`}>{colTickets.length}</span>
                </div>
                <div className="space-y-3">
                  {colTickets.map(t => (
                    <div key={t._id} data-testid={`ticket-card-${t._id}`}
                      className={`p-3 rounded-lg border ${ctx.borderColor} bg-black/10 hover:border-blue-500/40 transition-colors`}>
                      <div className="flex items-start justify-between mb-2 gap-2 cursor-pointer" onClick={() => setSelected(t)}>
                        <p className="text-sm font-medium leading-tight">{t.subject}</p>
                        <span className={`text-[10px] px-1.5 py-0.5 rounded-full border ${PRIORITY_COLORS[t.priority]}`}>{t.priority}</span>
                      </div>
                      <p className={`text-xs ${ctx.textSecondary} truncate`}>{t.customer_email}</p>
                      <div className={`mt-2 pt-2 border-t ${ctx.borderColor} flex items-center justify-between gap-2`}>
                        <span className="text-xs font-mono truncate">{t.reservation}</span>
                        <select
                          value={t.status}
                          onChange={(e) => updateStatus(t._id, e.target.value)}
                          onClick={(e) => e.stopPropagation()}
                          data-testid={`ticket-status-select-${t._id}`}
                          className={`text-[11px] px-2 py-0.5 rounded-full border-0 outline-none cursor-pointer ${ctx.hoverBg}`}
                        >
                          {STATUS_COLUMNS.map(s => <option key={s} value={s}>{s}</option>)}
                        </select>
                      </div>
                    </div>
                  ))}
                  {!colTickets.length && (
                    <p className={`text-center text-xs ${ctx.textSecondary} py-6`}>No tickets</p>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className={`${ctx.cardBg} rounded-xl border ${ctx.borderColor} overflow-x-auto`}>
          <table className="w-full text-sm">
            <thead><tr className={`border-b ${ctx.borderColor}`}>
              {['Subject', 'Customer', 'Reservation', 'Priority', 'Status', 'Assigned', 'Created'].map(h => (
                <th key={h} className={`text-left px-4 py-3 text-xs font-medium ${ctx.textSecondary} uppercase tracking-wider`}>{h}</th>
              ))}
            </tr></thead>
            <tbody>{tickets.map(t => (
              <tr key={t._id} className={`border-b ${ctx.borderColor} ${ctx.hoverBg}`}>
                <td className="px-4 py-3 font-medium text-sm">{t.subject}</td>
                <td className="px-4 py-3 text-xs">{t.customer_email}</td>
                <td className="px-4 py-3 font-mono text-xs">{t.reservation}</td>
                <td className="px-4 py-3"><span className={`text-xs px-2 py-0.5 rounded-full border ${PRIORITY_COLORS[t.priority]}`}>{t.priority}</span></td>
                <td className="px-4 py-3">
                  <select value={t.status} onChange={e => updateStatus(t._id, e.target.value)}
                    className={`text-xs px-2 py-1 rounded-full border-0 outline-none cursor-pointer ${ctx.hoverBg}`}>
                    {STATUS_COLUMNS.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </td>
                <td className="px-4 py-3 text-xs">{t.assigned_to || '—'}</td>
                <td className="px-4 py-3 text-xs">{new Date(t.created_date).toLocaleDateString()}</td>
              </tr>
            ))}</tbody>
          </table>
        </div>
      )}

      {/* Detail drawer */}
      {selected && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setSelected(null)}>
          <div className={`${ctx.cardBg} rounded-2xl border ${ctx.borderColor} max-w-lg w-full p-6`} onClick={e => e.stopPropagation()}>
            <h3 className="text-lg font-bold mb-1">{selected.subject}</h3>
            <p className={`text-xs ${ctx.textSecondary} mb-4`}>{selected.customer_email} · {selected.reservation}</p>
            <div className="space-y-3 mb-4">
              <div><span className={`text-xs ${ctx.textSecondary}`}>Priority:</span> <span className={`text-xs px-2 py-0.5 rounded-full border ${PRIORITY_COLORS[selected.priority]}`}>{selected.priority}</span></div>
              <div><span className={`text-xs ${ctx.textSecondary}`}>Status:</span> {selected.status}</div>
              <div><span className={`text-xs ${ctx.textSecondary}`}>Assigned:</span> {selected.assigned_to || 'Unassigned'}</div>
              <div className={`p-3 rounded-lg border ${ctx.borderColor} text-sm`}>{selected.notes}</div>
            </div>
            <div className="flex justify-end gap-2">
              <button onClick={() => setSelected(null)} className={`px-3 py-1.5 rounded-lg text-xs ${ctx.hoverBg}`}>Close</button>
              <button onClick={() => { updateStatus(selected._id, 'Resolved'); setSelected(null); }} className="px-3 py-1.5 rounded-lg bg-emerald-600 text-white text-xs">Mark Resolved</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
